<template>
  <div class="relative">
    <aside
      class="fixed top-0 left-0 h-screen flex flex-col bg-white dark:bg-slate-800/95 border-r border-neutral-200 dark:border-slate-700/50 transition-all duration-300"
      :class="{
        'w-[256px]': layoutStore.isExpanded,
        'w-[72px]': !layoutStore.isExpanded
      }"
    >
      <!-- Header -->
      <div
        class="shrink-0 p-4 flex items-center border-b border-neutral-200 dark:border-slate-700/50 bg-gradient-to-b from-white to-slate-50 dark:from-slate-800 dark:to-slate-800/90"
      >
        <div class="flex items-center justify-left w-full">
          <img
            v-if="layoutStore.isExpanded"
            :alt="t('common.logo_full')"
            class="w-40"
            src="@/assets/Logo.svg"
          />
          <img
            v-else
            :alt="t('common.logo_icon')"
            class="h-7"
            src="@/assets/CEETRAC-logo-icon.png"
          />
        </div>
        <!-- Expanded state -->
        <button
          v-if="layoutStore.isExpanded"
          class="p-1 hover:bg-neutral-100 dark:hover:bg-slate-800 rounded-lg absolute right-4 transition-colors duration-200"
          @click="layoutStore.toggleSidebar"
          :title="t('common.collapse_sidebar')"
        >
          <ChevronLeftIcon class="w-5 h-5 text-neutral-500 dark:text-slate-400" />
        </button>
      </div>

      <!-- Main Navigation -->
      <nav
        class="flex-1 overflow-y-auto p-2 space-y-1 bg-gradient-to-b from-white via-white to-slate-50 dark:from-slate-800 dark:via-slate-800 dark:to-slate-800/95"
      >
        <template v-for="item in mainNavigationItems" :key="item.label">
          <div class="space-y-1">
            <button
              class="w-full flex items-center justify-between px-2 py-2 text-sm rounded-[4px] transition-all duration-200"
              :class="{
                'bg-primary-50/80 dark:bg-slate-800 text-primary-700 dark:text-primary-400':
                  isActiveParent(item),
                'text-neutral-700 dark:text-slate-300 hover:bg-neutral-100 dark:hover:bg-slate-800/80 hover:text-neutral-900 dark:hover:text-slate-100':
                  !isActiveParent(item)
              }"
              @click="handleIconClick(item)"
              @mouseenter="showTooltip($event, item.label)"
              @mouseleave="hideTooltip"
            >
              <div
                class="flex items-center min-w-0"
                :class="{ 'justify-center w-full': !layoutStore.isExpanded }"
              >
                <component :is="item.icon" class="w-5 h-5 shrink-0" />
                <span v-if="layoutStore.isExpanded" class="ml-3 truncate">{{ item.label }}</span>
              </div>
              <ChevronDownIcon
                v-if="layoutStore.isExpanded && item.children"
                class="w-4 h-4 transition-transform text-neutral-500 dark:text-slate-400 shrink-0"
                :class="{ 'rotate-180': item.open }"
              />
            </button>
            <div v-if="layoutStore.isExpanded && item.open" class="pl-4 space-y-1">
              <template v-for="child in item.children">
                <router-link
                  v-if="child.hasPermission"
                  :key="child.label"
                  :to="child.path"
                  class="flex items-center px-2 py-2 text-sm rounded-[4px] transition-all duration-200"
                  :class="{
                    'text-primary-700 dark:text-primary-400 bg-primary-50/50 dark:bg-slate-800/50':
                      isActive(child.path),
                    'text-neutral-700 dark:text-slate-400 hover:bg-neutral-100 dark:hover:bg-slate-800/80 hover:text-neutral-900 dark:hover:text-slate-200':
                      !isActive(child.path)
                  }"
                >
                  {{ child.label }}
                </router-link>
              </template>
            </div>
          </div>
        </template>
      </nav>

      <!-- Footer Navigation -->
      <nav
        class="p-2 space-y-1 border-t border-neutral-200 dark:border-slate-700/50 bg-white dark:bg-slate-800"
      >
        <template v-for="item in footerNavigationItems" :key="item.label">
          <div class="space-y-1">
            <button
              class="w-full flex items-center justify-between px-2 py-2 text-sm rounded-[4px] transition-all duration-200"
              :class="{
                'bg-primary-50/80 dark:bg-slate-800 text-primary-700 dark:text-primary-400':
                  isActiveParent(item),
                'text-neutral-700 dark:text-slate-300 hover:bg-neutral-100 dark:hover:bg-slate-800/80 hover:text-neutral-900 dark:hover:text-slate-100':
                  !isActiveParent(item)
              }"
              @click="handleIconClick(item)"
              @mouseenter="showTooltip($event, item.label)"
              @mouseleave="hideTooltip"
            >
              <div
                class="flex items-center min-w-0"
                :class="{ 'justify-center w-full': !layoutStore.isExpanded }"
              >
                <component :is="item.icon" class="w-5 h-5 shrink-0" />
                <span v-if="layoutStore.isExpanded" class="ml-3 truncate">{{ item.label }}</span>
              </div>
              <ChevronDownIcon
                v-if="layoutStore.isExpanded"
                class="w-4 h-4 transition-transform text-neutral-500 dark:text-slate-400 shrink-0"
                :class="{ 'rotate-180': item.open }"
              />
            </button>
            <div v-if="layoutStore.isExpanded && item.open" class="pl-4 space-y-1">
              <router-link
                v-for="child in item.children"
                :key="child.label"
                :to="child.path"
                class="flex items-center px-2 py-2 text-sm rounded-[4px] transition-all duration-200"
                :class="{
                  'text-primary-700 dark:text-primary-400 bg-primary-50/50 dark:bg-slate-800/50':
                    isActive(child.path),
                  'text-neutral-700 dark:text-slate-400 hover:bg-neutral-100 dark:hover:bg-slate-800/80 hover:text-neutral-900 dark:hover:text-slate-200':
                    !isActive(child.path)
                }"
              >
                {{ child.label }}
              </router-link>
            </div>
          </div>
        </template>
      </nav>

      <!-- User Footer -->
      <div
        class="shrink-0 p-4 border-t border-neutral-200 dark:border-slate-700/50 bg-gradient-to-b from-slate-50 to-white dark:from-slate-800/90 dark:to-slate-800"
      >
        <div class="flex items-center justify-between">
          <div
            class="flex items-center min-w-0"
            :class="{ 'justify-center w-full': !layoutStore.isExpanded }"
            @mouseenter="
              !layoutStore.isExpanded && showTooltip($event, userStore.fullName || t('common.user'))
            "
            @mouseleave="hideTooltip"
          >
            <div
              class="w-8 h-8 rounded-full bg-neutral-200 dark:bg-slate-700 flex items-center justify-center shrink-0"
            >
              <UserIcon class="w-5 h-5 text-neutral-500 dark:text-slate-300" />
            </div>
            <div v-if="layoutStore.isExpanded" class="ml-3 min-w-0">
              <div class="text-sm font-medium text-neutral-900 dark:text-slate-200 truncate">
                {{ userStore.fullName }}
              </div>
              <div class="text-xs text-neutral-500 dark:text-slate-400 truncate">
                {{ userStore.email }}
              </div>
            </div>
          </div>
          <button
            v-if="layoutStore.isExpanded"
            @click="handleLogout"
            class="p-2 hover:bg-neutral-100 dark:hover:bg-slate-800 rounded-lg shrink-0 transition-colors duration-200"
            :title="t('common.logout')"
          >
            <ArrowRightOnRectangleIcon
              class="w-5 h-5 text-neutral-500 dark:text-slate-400 hover:text-neutral-700 dark:hover:text-slate-300"
            />
          </button>
        </div>
      </div>
    </aside>

    <!-- Collapsed Toggle Button -->
    <button
      v-if="!layoutStore.isExpanded"
      class="fixed top-4 left-[52px] p-2 bg-white dark:bg-slate-800 rounded-full shadow-md hover:shadow-lg transition-all duration-200 hover:bg-neutral-50 dark:hover:bg-slate-700"
      style="z-index: 9999"
      @click="layoutStore.toggleSidebar"
      @mouseenter="showTooltip($event, t('common.expand_sidebar'))"
      @mouseleave="hideTooltip"
    >
      <ChevronRightIcon class="w-4 h-4 text-neutral-500 dark:text-slate-300" />
    </button>

    <!-- Tooltip -->
    <BaseTooltip
      v-if="hoveredItem && !layoutStore.isExpanded"
      :text="hoveredItem"
      :x="tooltipPosition.x"
      :y="tooltipPosition.y"
    />
  </div>
</template>

<script setup lang="ts">
import {
  ArrowRightOnRectangleIcon,
  ArrowsRightLeftIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentPlusIcon,
  FolderIcon,
  QuestionMarkCircleIcon,
  UserIcon
} from '@heroicons/vue/24/outline';
import type { Component } from 'vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { authService } from '../services/authService';
import { useLayoutStore } from '../stores/layout';
import { useUserStore } from '../stores/user';
import BaseTooltip from './BaseTooltip.vue';
import { usePermissions } from '../composables/usePermissions';

interface NavigationItem {
  label: string;
  path?: string;
  icon: Component;
  children?: Array<{ label: string; path: string; hasPermission?: boolean }>;
  open?: boolean;
}

const route = useRoute();
const router = useRouter();
const layoutStore = useLayoutStore();
const userStore = useUserStore();
const { t, locale } = useI18n();
const permissions = usePermissions();

// Tooltip logic
const hoveredItem = ref<string | null>(null);
const tooltipPosition = ref({ x: 0, y: 0 });

const showTooltip = (event: MouseEvent, label: string) => {
  if (layoutStore.isExpanded) return;

  const target = event.currentTarget as HTMLElement;
  const icon = target.querySelector('svg');
  const rect = icon ? icon.getBoundingClientRect() : target.getBoundingClientRect();

  tooltipPosition.value = {
    x: rect.right + 10,
    y: rect.top + rect.height / 2
  };
  hoveredItem.value = label;
};

const hideTooltip = () => {
  hoveredItem.value = null;
};

// Funktion zum Generieren der sprachabhängigen Pfade
const getLocalizedPath = (path: string) => {
  // Entferne führenden Sprachcode, falls vorhanden
  const pathWithoutLang = path.replace(/^\/[a-z]{2}\//, '/');
  // Füge aktuellen Sprachcode hinzu
  return `/${locale.value}${pathWithoutLang}`;
};

const handleLogout = async () => {
  await authService.logout();
  await router.push(getLocalizedPath('/de/login'));
};

// Berechtigungsprüfung für Admin-Funktionen
const canAccessAdmin = computed(() => {
  const currentMandant = authService.getMandant();
  const mandanten = authService.getMandanten();
  const isCurrentMandantAdmin =
    mandanten.find((m: { mandant: string; adminKz: boolean }) => m.mandant === currentMandant)
      ?.adminKz || false;
  return userStore.adminKz || isCurrentMandantAdmin;
});

// State für das Auf-/Zuklappen der Navigation
const navigationState = ref({
  bewegungsdaten: false,
  stammdaten: false,
  reports: false,
  hilfe: false,
  benutzer: false
});

// Main navigation (top) - Alle Menüpunkte anzeigen, aber ohne Berechtigung ausgegraut
const mainNavigationItems = computed<NavigationItem[]>(() => {
  const items: NavigationItem[] = [];

  // Bewegungsdaten - alle Untermenüs anzeigen, aber mit Berechtigungsstatus
  const bewegungsdatenChildren = [
    {
      label: t('navigation.bewegungsdaten_children.abholungen'),
      path: getLocalizedPath('/de/bewegungsdaten/abholungen'),
      hasPermission: permissions.canViewAbholungen.value
    },
    {
      label: t('navigation.bewegungsdaten_children.einlieferungen'),
      path: getLocalizedPath('/de/bewegungsdaten/einlieferungen'),
      hasPermission: permissions.canViewEinlieferungen.value
    },
    {
      label: t('navigation.bewegungsdaten_children.ablieferungen'),
      path: getLocalizedPath('/de/bewegungsdaten/ablieferungen'),
      hasPermission: permissions.canViewAblieferungen.value
    },
    {
      label: t('navigation.bewegungsdaten_children.bestaende'),
      path: getLocalizedPath('/de/bewegungsdaten/bestaende'),
      hasPermission: permissions.canViewBestaende.value
    },
    {
      label: t('navigation.bewegungsdaten_children.nachweisbescheinigungen'),
      path: getLocalizedPath('/de/bewegungsdaten/nachweisbescheinigungen'),
      hasPermission: permissions.canViewNachweise.value
    },
    {
      label: t('navigation.bewegungsdaten_children.tankstelle_bestand'),
      path: getLocalizedPath('/de/bewegungsdaten/tankstelle-bestand'),
      hasPermission: permissions.canAccessCockpit.value
    },
    {
      label: t('navigation.bewegungsdaten_children.nachweisverfolgung'),
      path: getLocalizedPath('/de/bewegungsdaten/nachweisverfolgung'),
      hasPermission: permissions.canViewNachweisverfolgung.value
    },
    {
      label: t('navigation.bewegungsdaten_children.nachweisverteilung'),
      path: getLocalizedPath('/de/bewegungsdaten/nachweisverteilung'),
      hasPermission: permissions.canAccessNachweisverteilung.value
    },
    {
      label: t('navigation.bewegungsdaten_children.zertifikate'),
      path: getLocalizedPath('/de/bewegungsdaten/nachweise'),
      hasPermission: permissions.canViewZertifikate.value
    },
    {
      label: t('navigation.bewegungsdaten_children.bilanzen'),
      path: getLocalizedPath('/de/bewegungsdaten/bilanzen'),
      hasPermission: permissions.canViewBilanzen.value
    },
    {
      label: t('navigation.bewegungsdaten_children.pocs'),
      path: getLocalizedPath('/de/bewegungsdaten/pocs'),
      hasPermission: permissions.canViewPocs.value
    },
    {
      label: t('navigation.bewegungsdaten_children.import'),
      path: getLocalizedPath('/de/bewegungsdaten/import'),
      hasPermission:
        permissions.canImportInbound.value ||
        permissions.canImportOutbound.value ||
        permissions.canEditZertifikate.value
    }
  ];

  if (bewegungsdatenChildren.some((x) => x.hasPermission)) {
    items.push({
      label: t('navigation.bewegungsdaten'),
      icon: ArrowsRightLeftIcon,
      children: bewegungsdatenChildren,
      open: navigationState.value.bewegungsdaten
    });
  }

  // Stammdaten - alle Untermenüs anzeigen, aber mit Berechtigungsstatus
  const stammdatenChildren = [
    {
      label: t('navigation.stammdaten_children.allgemein'),
      path: getLocalizedPath('/de/stammdaten/allgemein'),
      hasPermission: permissions.canViewMandantAllgemein.value
    },
    {
      label: t('navigation.stammdaten_children.partner'),
      path: getLocalizedPath('/de/stammdaten/tauschpartner'),
      hasPermission: permissions.canViewPartner.value
    },
    {
      label: t('navigation.stammdaten_children.produkte'),
      path: getLocalizedPath('/de/stammdaten/produkte'),
      hasPermission: permissions.canViewProdukte.value
    },
    {
      label: t('navigation.stammdaten_children.standorte'),
      path: getLocalizedPath('/de/stammdaten/standorte'),
      hasPermission: permissions.canViewStandorte.value
    },
    {
      label: t('navigation.stammdaten_children.zertifizierungen'),
      path: getLocalizedPath('/de/stammdaten/zertifizierungen'),
      hasPermission: permissions.canViewZertifizierungen.value
    },
    {
      label: t('navigation.stammdaten_children.import'),
      path: getLocalizedPath('/de/stammdaten/import'),
      hasPermission:
        permissions.canEditProdukte.value || permissions.canEditStandorte.value || permissions.canEditPartner.value
    }
  ];

  if (stammdatenChildren.some((x) => x.hasPermission)) {
    items.push({
      label: t('navigation.stammdaten'),
      icon: FolderIcon,
      children: stammdatenChildren,
      open: navigationState.value.stammdaten
    });
  }

  // Reports - alle Untermenüs anzeigen, aber mit Berechtigungsstatus
  const reportsChildren = [
    {
      label: t('navigation.reports_children.liste'),
      path: getLocalizedPath('/de/reports/liste'),
      hasPermission: permissions.canAccessReports.value
    },
    {
      label: t('navigation.reports_children.ytdOverview'),
      path: getLocalizedPath('/de/reports/ytd'),
      hasPermission: permissions.canAccessYtdReport.value
    }
  ];

  if (reportsChildren.some((x) => x.hasPermission)) {
    items.push({
      label: t('navigation.reports'),
      icon: ChartBarIcon,
      children: reportsChildren,
      open: navigationState.value.reports
    });
  }

  if (permissions.canAccessGenericImport.value) {
    items.push({
      label: t('navigation.import'),
      icon: DocumentPlusIcon,
      path: getLocalizedPath('/de/import')
    });
  }

  return items;
});

// Footer navigation (bottom)
const footerNavigationItems = computed<NavigationItem[]>(() => [
  {
    label: t('navigation.help'),
    icon: QuestionMarkCircleIcon,
    open: navigationState.value.hilfe,
    children: [
      {
        label: t('navigation.help_children.documentation'),
        path: getLocalizedPath('/de/hilfe/dokumentation')
      },
      {
        label: t('navigation.help_children.faq'),
        path: getLocalizedPath('/de/hilfe/faq')
      },
      {
        label: t('navigation.help_children.videos'),
        path: getLocalizedPath('/de/hilfe/hilfevideos')
      }
    ]
  },
  {
    label: t('navigation.user'),
    icon: UserIcon,
    open: navigationState.value.benutzer,
    children: [
      {
        label: t('navigation.user_children.dashboard'),
        path: getLocalizedPath('/de/benutzer/dashboard')
      },
      {
        label: t('navigation.user_children.change_password'),
        path: getLocalizedPath('/de/benutzer/passwort')
      },
      {
        label: t('navigation.user_children.settings'),
        path: getLocalizedPath('/de/benutzer/einstellungen')
      },
      // Berechtigungen für Verbraucher nicht anzeigen
      ...(userStore.verbraucherKz
        ? []
        : [
            {
              label: t('navigation.user_children.permissions'),
              path: getLocalizedPath('/de/benutzer/berechtigungen')
            }
          ]),
      // Benutzeradministration nur für Admins anzeigen
      ...(canAccessAdmin.value || userStore.verbraucherKz
        ? [
            ...(canAccessAdmin.value || userStore.verbraucherKz
              ? [
                  {
                    label: t('navigation.user_children.administration'),
                    path: getLocalizedPath('/de/benutzer/administration')
                  }
                ]
              : []),
            ...(canAccessAdmin.value
              ? [
                  {
                    label: t('navigation.user_children.permission_management'),
                    path: getLocalizedPath('/de/benutzer/berechtigungsverwaltung')
                  }
                ]
              : []),
            // Mandantenverwaltung nur für System-Admins sichtbar
            ...(userStore.adminKz
              ? [
                  {
                    label: t('navigation.user_children.mandanten'),
                    path: getLocalizedPath('/de/benutzer/mandanten')
                  }
                ]
              : [])
          ]
        : []),
      {
        label: t('navigation.user_children.release_notes'),
        path: getLocalizedPath('/de/benutzer/release-notes')
      }
    ]
  }
]);

const isActive = (path?: string) => {
  if (!path) return false;
  return route.path === path;
};

const isActiveParent = (item: NavigationItem) => {
  if (!item.children) return false;
  return item.children.some((child) => isActive(child.path));
};

const handleIconClick = async (item: NavigationItem) => {
  if (item.path) {
    await router.push(item.path);
  } else if (!layoutStore.isExpanded) {
    // Sidebar erweitern und Kategorie öffnen
    await layoutStore.toggleSidebar();
    toggleNavigationItem(item.label);

    // Navigiere zum ersten verfügbaren Untermenüpunkt
    if (item.children && item.children.length > 0) {
      const firstAvailableChild = item.children.find((child) => child.hasPermission !== false);
      if (firstAvailableChild) {
        await router.push(firstAvailableChild.path);
      }
    }
  } else {
    // Kategorie auf-/zuklappen
    toggleNavigationItem(item.label);
  }
};

const toggleNavigationItem = (label: string) => {
  switch (label) {
    case t('navigation.bewegungsdaten'):
      navigationState.value.bewegungsdaten = !navigationState.value.bewegungsdaten;
      break;
    case t('navigation.stammdaten'):
      navigationState.value.stammdaten = !navigationState.value.stammdaten;
      break;
    case t('navigation.reports'):
      navigationState.value.reports = !navigationState.value.reports;
      break;
    case t('navigation.help'):
      navigationState.value.hilfe = !navigationState.value.hilfe;
      break;
    case t('navigation.user'):
      navigationState.value.benutzer = !navigationState.value.benutzer;
      break;
  }
};
</script>
